/**
 * Primary navigation behaviour.
 *
 * Desktop: each top level item with children owns a disclosure button that
 * controls a mega menu panel. Mouse users can also hover, but hover only ever
 * mirrors the same state the keyboard sets, so `aria-expanded` stays truthful.
 *
 * Mobile: a modal drawer with a focus trap, plus per-column accordions.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isVisible = (el) => !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);

/* ------------------------------------------------------------------ */
/* Desktop mega menu                                                    */
/* ------------------------------------------------------------------ */

class MegaMenu {
  constructor(nav) {
    this.nav = nav;
    this.hoverTimer = null;
    this.triggers = Array.from(nav.querySelectorAll("[aria-controls][aria-expanded]"));

    this.triggers.forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute("aria-controls"));
      if (!panel) return;

      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.toggle(trigger, trigger.getAttribute("aria-expanded") !== "true");
      });

      trigger.addEventListener("keydown", (event) => {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          this.open(trigger);
          this.focusPanelLink(panel, 0);
        }
      });

      const item = trigger.closest(".nav__item");
      if (item) {
        item.addEventListener("mouseenter", () => this.hoverOpen(trigger));
        item.addEventListener("mouseleave", (event) => this.hoverClose(trigger, event));
      }

      panel.addEventListener("keydown", (event) => this.onPanelKeydown(event, trigger, panel));
      this.bindColumnFades(panel);
    });

    window.addEventListener("resize", () => {
      const openTrigger = this.openTrigger();
      if (!openTrigger) return;
      this.syncPanelFades(this.panelFor(openTrigger));
    });

    nav.addEventListener("focusout", () => {
      const openTrigger = this.openTrigger();
      if (!openTrigger) return;

      const item = openTrigger.closest(".nav__item");
      if (!item) return;

      window.requestAnimationFrame(() => {
        if (this.openTrigger() !== openTrigger) return;
        if (item.contains(document.activeElement)) return;
        this.close(openTrigger, { restoreFocus: false });
      });
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      const openTrigger = this.openTrigger();
      if (openTrigger) {
        event.preventDefault();
        this.close(openTrigger);
      }
    });

    document.addEventListener("click", (event) => {
      const openTrigger = this.openTrigger();
      if (!openTrigger) return;
      if (!this.nav.contains(event.target)) {
        this.close(openTrigger, { restoreFocus: false });
      }
    });
  }

  openTrigger() {
    return this.triggers.find((t) => t.getAttribute("aria-expanded") === "true") || null;
  }

  panelFor(trigger) {
    return document.getElementById(trigger.getAttribute("aria-controls"));
  }

  toggle(trigger, shouldOpen) {
    if (shouldOpen) {
      this.open(trigger);
    } else {
      this.close(trigger);
    }
  }

  open(trigger) {
    const current = this.openTrigger();
    if (current && current !== trigger) {
      this.close(current, { restoreFocus: false });
    }

    const panel = this.panelFor(trigger);
    if (!panel) return;

    trigger.setAttribute("aria-expanded", "true");
    panel.hidden = false;
    document.body.classList.add("nav-panel-open");
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.syncPanelFades(panel));
    });
  }

  bindColumnFades(panel) {
    panel.querySelectorAll(".nav__column").forEach((column) => {
      if (column.dataset.fadeBound) return;
      column.dataset.fadeBound = "1";
      column.addEventListener("scroll", () => this.syncColumnFade(column), { passive: true });

      const shell = column.closest(".nav__column-shell");
      const more = shell && shell.querySelector(".nav__column-more");
      if (!more || more.dataset.fadeBound) return;
      more.dataset.fadeBound = "1";
      more.addEventListener("click", () => {
        column.scrollTo({ top: column.scrollHeight, behavior: "smooth" });
      });
    });
  }

  syncPanelFades(panel) {
    if (!panel) return;
    panel.querySelectorAll(".nav__column").forEach((column) => this.syncColumnFade(column));
  }

  syncColumnFade(column) {
    const shell = column.closest(".nav__column-shell") || column;
    const showMore =
      column.scrollHeight > column.clientHeight + 1 &&
      column.scrollTop + column.clientHeight < column.scrollHeight - 1;

    if (!showMore) {
      const more = shell.querySelector(".nav__column-more");
      if (more && document.activeElement === more) {
        const panel = column.closest(".nav__panel");
        const trigger = panel && this.triggers.find((t) => this.panelFor(t) === panel);
        if (trigger) trigger.focus({ preventScroll: true });
      }
    }

    shell.classList.toggle("nav__column-shell--fade-more", showMore);
  }

  close(trigger, { restoreFocus = true } = {}) {
    const panel = this.panelFor(trigger);
    if (!panel) return;

    trigger.setAttribute("aria-expanded", "false");
    panel.hidden = true;
    document.body.classList.remove("nav-panel-open");

    if (restoreFocus) {
      trigger.focus();
    }
  }

  hoverOpen(trigger) {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    window.clearTimeout(this.hoverTimer);
    this.hoverTimer = window.setTimeout(() => this.open(trigger), 80);
  }

  hoverClose(trigger, event) {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    const item = trigger.closest(".nav__item");
    if (item && event && event.relatedTarget && item.contains(event.relatedTarget)) return;

    window.clearTimeout(this.hoverTimer);
    this.hoverTimer = window.setTimeout(() => {
      if (item && (item.contains(document.activeElement) || item.matches(":hover"))) return;
      this.close(trigger, { restoreFocus: false });
    }, 200);
  }

  panelLinks(panel) {
    return Array.from(panel.querySelectorAll(".nav__link"));
  }

  focusPanelLink(panel, index) {
    const links = this.panelLinks(panel);
    if (!links.length) return;
    const clamped = (index + links.length) % links.length;
    links[clamped].focus();
  }

  onPanelKeydown(event, trigger, panel) {
    const links = this.panelLinks(panel);
    const index = links.indexOf(document.activeElement);

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.focusPanelLink(panel, index + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        if (index <= 0) {
          trigger.focus();
        } else {
          this.focusPanelLink(panel, index - 1);
        }
        break;
      case "Home":
        event.preventDefault();
        this.focusPanelLink(panel, 0);
        break;
      case "End":
        event.preventDefault();
        this.focusPanelLink(panel, links.length - 1);
        break;
      default:
        break;
    }
  }
}

/* ------------------------------------------------------------------ */
/* Mobile drawer                                                        */
/* ------------------------------------------------------------------ */

class MobileNav {
  constructor(drawer, openButton) {
    this.drawer = drawer;
    this.dialog = drawer.querySelector(".mobile-nav__inner") || drawer;
    this.openButton = openButton;
    this.closeButton = drawer.querySelector(".js-mobile-nav-close");

    openButton.addEventListener("click", () => this.toggle());

    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.close());
    }

    drawer.querySelectorAll(".js-mobile-nav-toggle").forEach((toggle) => {
      toggle.addEventListener("click", () => this.toggleSection(toggle));
    });

    document.addEventListener("keydown", (event) => {
      if (!this.isOpen()) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === "Tab") {
        this.trapTab(event);
      }
    });

    // The drawer is a desktop-less feature; close it if the viewport grows.
    window.addEventListener("resize", () => {
      if (this.isOpen() && !isVisible(this.openButton)) {
        this.close({ restoreFocus: false });
      }
    });
  }

  isOpen() {
    return !this.drawer.hidden;
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.drawer.hidden = false;
    this.openButton.setAttribute("aria-expanded", "true");
    this.openButton.setAttribute("aria-label", "Stäng menyn.");
    document.body.classList.add("mobile-nav-open");
    document.documentElement.style.overflow = "hidden";

    const label = this.openButton.querySelector(".hamburger__text");
    if (label) label.textContent = "Stäng";

    (this.closeButton || this.dialog).focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    this.drawer.hidden = true;
    this.openButton.setAttribute("aria-expanded", "false");
    this.openButton.setAttribute("aria-label", "Öppna menyn.");
    document.body.classList.remove("mobile-nav-open");
    document.documentElement.style.overflow = "";

    const label = this.openButton.querySelector(".hamburger__text");
    if (label) label.textContent = "Meny";

    if (restoreFocus) {
      this.openButton.focus();
    }
  }

  toggleSection(toggle) {
    const list = document.getElementById(toggle.getAttribute("aria-controls"));
    if (!list) return;

    const expanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!expanded));
    list.hidden = expanded;
  }

  focusableItems() {
    return Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(isVisible);
  }

  trapTab(event) {
    const items = this.focusableItems();
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (!this.dialog.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

/* ------------------------------------------------------------------ */

function syncHeaderHeight(header) {
  document.documentElement.style.setProperty("--header-h", `${header.offsetHeight}px`);
}

function init() {
  const header = document.querySelector(".header");
  if (header) {
    syncHeaderHeight(header);
    window.addEventListener("resize", () => syncHeaderHeight(header));
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => syncHeaderHeight(header));
    }
  }

  const nav = document.querySelector(".nav");
  if (nav) {
    new MegaMenu(nav);
  }

  const drawer = document.getElementById("mobile-nav");
  const openButton = document.querySelector(".js-mobile-nav-open");
  if (drawer && openButton) {
    const mobileNav = new MobileNav(drawer, openButton);
    // Exposed so other scripts (search toggle) can close the drawer.
    window.ericaMobileNav = mobileNav;
  }

  // Keep transitions off when the user asked for reduced motion, even for
  // inline styles set elsewhere.
  if (prefersReducedMotion()) {
    document.documentElement.classList.add("reduced-motion");
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
